import { BoxPosition } from '@/generated/model/boxPosition'
import { Borders } from '@/generated/model/gripper'
import { getAlignedGroupsInline } from '@/libs/pattern/layerSort'
import { MachineData, PatternEditorState } from '@/libs/pattern/patternEditor'
import * as utils from '@/libs/pattern/utils'
import { BoxDef } from '@/libs/pattern/boxDef'

interface Point {
  x: number
  y: number
}

export const calculateGroupBorders = (state: PatternEditorState, machineData: MachineData, boxDef: BoxDef)
  : Array<Array<{ x: number, y: number, pusher?: boolean, doublePlace?: boolean }>> => {
  const result = []
  for (let i = 0; i < state.groups.length; i++) {
    const group = state.groups[i].map(id => state.positions.find(p => p.id === id))
    const pusherGrouped = state.pusherGroups.length > 0 && state.pusherGroups[i] === 1
    const doublePlace = state.gripperSections.length > 0 && state.gripperSections[i].some(s => s !== 0)
    // console.log('calculateGroupBorders - positions: ', JSON.stringify(state.positions))
    // console.log('calculateGroupBorders - pusherGrouped: ', pusherGrouped)
    // console.log('calculateGroupBorders - doublePlace: ', doublePlace, JSON.stringify(state.gripperSections[i]))
    if (doublePlace) {
      if (state.gripperSections[i].length === 4) {
        const size = group.length / 2
        for (let j = 0; j < group.length; j += size) {
          const slice = group.slice (j, j + size)
          // console.log ('calculateGroupBorders - slice: ', JSON.stringify (slice), size, j)
          const b = getBorders (slice, boxDef)
          result.push ([...getBorderPoints (b, pusherGrouped, doublePlace)])
        }
      } else if (state.gripperSections[i].length === 3 && group.length > 2) {
        const isSSL = machineData.boxOrientationOnConveyor === 'SHORT_SIDE_LEADING'
        // @ts-ignore
        const aligned = getAlignedGroupsInline (group, (isSSL) ? boxDef.width : boxDef.height, (isSSL) ? boxDef.height : boxDef.width, isSSL)
        const group1 = (aligned.vertical.length > 1) ? aligned.vertical : ((aligned.horizontal.length > 1) ? aligned.horizontal : [])
        const a = getBorders (group1, boxDef)
        const b = getBorders (aligned.rest, boxDef)
        // console.log('calculateGroupBorders - 3 sections: ', JSON.stringify(a), JSON.stringify(b))
        result.push ([...getBorderPoints (a, pusherGrouped, doublePlace)])
        result.push ([...getBorderPoints (b, pusherGrouped, doublePlace)])
      } else {
        // DP 2 section or DP Inline3 with 2 boxes
        for (const p of group) {
          const b = getBorders ([p], boxDef)
          result.push ([...getBorderPoints (b, pusherGrouped, doublePlace)])
        }
      }
    } else if (machineData.freeBoxGrouping) {
      // @ts-ignore
      const test = getConnectedBoxes(group, boxDef.width, boxDef.height)
      for (const g of test) {
        const segments: Array<[Point, Point]> = []
        for (const box of g) {
          const borderPoints = getBorderPoints(getBorders([box], boxDef), false, false)
          segments.push (
            [borderPoints[0], borderPoints[1]],
            [borderPoints[1], borderPoints[2]],
            [borderPoints[2], borderPoints[3]],
            [borderPoints[3], borderPoints[0]]
          )
        }
        const outerSegments = removeInternalEdges(segments)
        const outline = sortOutlinePoints(outerSegments)
        if (outline.length > 0) {
          result.push(outline)
        }
      }
    } else {
      // single place
      const b = getBorders (group, boxDef)
      result.push ([...getBorderPoints (b, pusherGrouped, doublePlace)])
    }
  }
  // console.log('calculateGroupBorders - result: ', JSON.stringify(result))
  return result
}

export function getBorders (positions: Array<(BoxPosition | undefined)>, boxDef: BoxDef, custom: boolean = false): Borders {
  let width: number
  let height: number
  const borders = positions.filter(p => p != null).map(p => {
    width = boxDef.width * utils.projX(p!.rotation!) + boxDef.height * utils.projY(p!.rotation!)
    height = boxDef.height * utils.projX(p!.rotation!) + boxDef.width * utils.projY(p!.rotation!)
    return {
      top: p!.y! - height / 2,
      right: (custom) ? p!.x! - width / 2 : p!.x! + width / 2,
      bottom: p!.y! + height / 2,
      left: (custom) ? p!.x! + width / 2 : p!.x! - width / 2
    }
  })
  return {
    top: Math.min(...borders.map(p => p.top)),
    right: (custom) ? Math.min(...borders.map(p => p.right)) : Math.max(...borders.map(p => p.right)),
    bottom: Math.max(...borders.map(p => p.bottom)),
    left: (custom) ? Math.max(...borders.map(p => p.left)) : Math.min(...borders.map(p => p.left))
  }
}

export function getBorderPoints (b: Borders, pusher?: boolean, doublePlace?: boolean)
: Array<{ x: number, y: number, pusher?: boolean, doublePlace?: boolean }> {
  return [
    { x: b.left, y: b.top, pusher: pusher, doublePlace: doublePlace },
    { x: b.right, y: b.top, pusher: pusher, doublePlace: doublePlace },
    { x: b.right, y: b.bottom, pusher: pusher, doublePlace: doublePlace },
    { x: b.left, y: b.bottom, pusher: pusher, doublePlace: doublePlace }
  ]
}

function getConnectedBoxes (boxes: BoxPosition[], length: number, width: number): BoxPosition[][] {
  const groups: BoxPosition[][] = []
  const visited = new Set<number>()

  function findConnected (index: number, group: BoxPosition[]) {
    if (visited.has(index)) {
      return
    }
    visited.add(index)
    const b1 = boxes[index]
    group.push(b1)

    const angle1 = b1.rotation!
    const w = Math.abs (
      length * Math.round (Math.cos (angle1 / 180 * Math.PI)) +
      width * Math.round (Math.sin (angle1 / 180 * Math.PI))
    )
    const h = Math.abs (
      width * Math.round (Math.cos (angle1 / 180 * Math.PI)) +
      length * Math.round (Math.sin (angle1 / 180 * Math.PI))
    )

    for (let i = 0; i < boxes.length; i++) {
      if (!visited.has(i)) {
        const b2 = boxes[i]
        const distance = Math.sqrt ((b1.x! - b2.x!) ** 2 + (b1.y! - b2.y!) ** 2)
        // console.log(`getConnectedBoxes - distance : ${distance} (w: ${w}, h: ${h}`)
        if (distance === w || distance === h) {
          findConnected(i, group)
        }
      }
    }
  }

  for (let i = 0; i < boxes.length; i++) {
    if (!visited.has(i)) {
      const group: BoxPosition[] = []
      findConnected(i, group)
      groups.push(group)
    }
  }
  return groups
}

function removeInternalEdges (segments: Array<[Point, Point]>)
  : Array<[Point, Point]> {
  const edgeMap = new Map<string, number>()
  function edgeKey (p1: Point, p2: Point): string {
    const[minP, maxP] = [p1, p2].sort((a, b) =>
      a.x === b.x ? a.y - b.y : a.x - b.x
    )
    return `${minP.x},${minP.y}-${maxP.x},${maxP.y}`
  }
  for (const [p1, p2] of segments) {
    const key = edgeKey(p1, p2)
    edgeMap.set(key, (edgeMap.get(key) || 0) + 1)
  }
  return segments.filter(([p1, p2]) => {
    const key = edgeKey(p1, p2)
    return edgeMap.get(key) === 1
  })
}

function sortOutlinePoints (segments: Array<[Point, Point]>): Array<{x: number, y: number}> {
  if (segments.length === 0) {
    return []
  }
  const outline: Point[] = []
  const remaining = [...segments]
  const current = remaining.pop()
  // @ts-ignore
  outline.push(current[0], current[1])

  while (remaining.length > 0) {
    const lastPoint = outline[outline.length - 1]
    const nextIndex = remaining.findIndex(([p1, p2]) =>
      (p1.x === lastPoint.x && p1.y === lastPoint.y) ||
      (p2.x === lastPoint.x && p2.y === lastPoint.y)
    )
    if (nextIndex < 0) {
      break
    }
    const next = remaining.splice(nextIndex, 1)[0]
    if (next[0].x === lastPoint.x && next[0].y === lastPoint.y) {
      outline.push(next[1])
    } else {
      outline.push(next[0])
    }
  }

  return outline.map(p => ({ x: p.x, y: p.y }))
}
